// tableColumnUtils.js

import {
    DEFAULT_CORNER_WIDTH,
    DEFAULT_FIELD_WIDTH,
    DEFAULT_AUTONUMBER_WIDTH,
    DEFAULT_AUTOKEY_WIDTH,
    DEFAULT_TIMESTAMP_FIELD_WIDTH,
} from 'views/table/hooks/useTableSettings';

const extractViewColumnWidths = (source) => {
    if (!source || typeof source !== 'object') return {};
    return Object.entries(source).reduce((acc, [key, value]) => {
        if (typeof value === 'number' && value > 0) acc[key] = value;
        return acc;
    }, {});
};

const getAllowedWidthKeys = (visibleFieldKeys) => (
    new Set(['open', 'autonumber', 'autokey', 'title', ...(visibleFieldKeys || [])])
);

const normalizeColumnWidths = (columnWidths, allowedWidthKeys) => (
    Object.entries(columnWidths || {}).reduce((acc, [key, value]) => {
        if (allowedWidthKeys.has(key)) acc[key] = value;
        return acc;
    }, {})
);

const mergeEffectiveColumnWidths = (normalized, overrides, allowedWidthKeys) => (
    Object.entries({ ...(normalized || {}), ...(overrides || {}) })
        .reduce((acc, [key, value]) => {
            if (allowedWidthKeys.has(key) && typeof value === 'number' && value > 0) {
                acc[key] = value;
            }
            return acc;
        }, {})
);

const buildColumnWidthPayload = (normalizedColumnWidths) => ({
    columnWidths: Object.keys(normalizedColumnWidths || {}).length
        ? normalizedColumnWidths
        : null,
});

const buildTableColumns = ({
    schemaFieldMap,
    visibleFieldKeys,
    effectiveColumnWidths,
}) => {
    const showAutoNumber = Array.isArray(visibleFieldKeys)
        && visibleFieldKeys.includes('autonumber');
    const showAutoKey = Array.isArray(visibleFieldKeys)
        && visibleFieldKeys.includes('autokey');

    const base = [
        { key: 'open', label: '', width: effectiveColumnWidths?.open || DEFAULT_CORNER_WIDTH },
        { key: 'title', label: 'Title', width: effectiveColumnWidths?.title || DEFAULT_FIELD_WIDTH },
    ];

    if (showAutoNumber) {
        base.splice(1, 0, {
            key: 'autonumber',
            label: 'ID',
            width: effectiveColumnWidths?.autonumber || DEFAULT_AUTONUMBER_WIDTH,
        });
    }

    if (showAutoKey) {
        base.splice(showAutoNumber ? 2 : 1, 0, {
            key: 'autokey',
            label: 'Key',
            width: effectiveColumnWidths?.autokey || DEFAULT_AUTOKEY_WIDTH,
        });
    }

    const derived = (visibleFieldKeys || [])
        .filter((fieldKey) => fieldKey !== 'autonumber' && fieldKey !== 'autokey')
        .map((fieldKey) => {
        const fieldDef = schemaFieldMap?.get(fieldKey) || null;
        const fallbackLabel = fieldKey === 'dateCreated'
            ? 'Created'
            : (fieldKey === 'dateModified' ? 'Modified' : fieldKey);
        const label = fieldDef?.label
            || fieldDef?.name
            || fallbackLabel;
        const defaultWidth = fieldKey === 'dateCreated' || fieldKey === 'dateModified'
            ? DEFAULT_TIMESTAMP_FIELD_WIDTH
            : DEFAULT_FIELD_WIDTH;
        const width = effectiveColumnWidths?.[fieldKey] || defaultWidth;
        return { key: fieldKey, label, width };
    });

    return [...base, ...derived];
};

export {
    buildColumnWidthPayload,
    buildTableColumns,
    extractViewColumnWidths,
    getAllowedWidthKeys,
    mergeEffectiveColumnWidths,
    normalizeColumnWidths,
};
